import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

import { Skeleton } from '@mui/material'
import { formatDate } from 'commons/utils/date'
import { type HeadItemType, Modal, Pagination, Table } from 'components'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getPendingEDIs } from 'services/salesOrder'
import type { PendingEDI } from 'store/SalesOrderContext/types/PendingEDI'

import * as S from './styles'

export interface PropsModal {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
}

const ModalPendingEDI = ({ open, setOpen }: PropsModal) => {
  const { orderNumber } = useParams()
  const [loading, setLoading] = useState(true)
  const [pendingIssues, setPendingIssues] = useState<PendingEDI[]>([])

  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [totalItems, setTotalItems] = useState<number>(0)
  const PAGE_SIZE = 10

  const headValues: HeadItemType[] = [
    { columnName: 'Data', label: 'Data' },
    { columnName: 'Hora', label: 'Hora' },
    { columnName: 'Material', label: 'Material' },
    { columnName: 'Matriz', label: 'Matriz' },
    { columnName: 'Centro', label: 'Centro' },
    { columnName: 'Quantidade', label: 'Quantidade' },
    { columnName: 'Mensagem', label: 'Mensagem' },
  ]

  const renderBody = () => {
    if (loading) return renderSkeleton()
    if (!pendingIssues.length) return []

    return pendingIssues.map((pendingIssue, index) => {
      return {
        content: [
          formatDate(pendingIssue?.dt_cadt) ?? '--',
          formatDate(pendingIssue?.dt_cadt, 'HH[h]mm') ?? '--',
          pendingIssue?.cd_matr ?? '--',
          pendingIssue?.cd_mtrz_matr ?? '--',
          pendingIssue?.cd_cent_matr ?? '--',
          pendingIssue?.qt_item ?? '--',
          pendingIssue?.ds_msgm ?? '--',
        ],
        id: `tracking-item-${index}`,
      }
    })
  }

  const renderSkeleton = () => {
    const skeleton = []
    for (let i = 0; i < 3; i++) {
      skeleton.push({
        content: [
          <Skeleton height={30} key="pendingIssue-skeleton-1" width={120} />,
          <Skeleton height={30} key="pendingIssue-skeleton-2" width={60} />,
          <Skeleton height={30} key="pendingIssue-skeleton-2" width={100} />,
          <Skeleton height={30} key="pendingIssue-skeleton-2" width={100} />,
          <Skeleton height={30} key="pendingIssue-skeleton-2" width={100} />,
          <Skeleton height={30} key="pendingIssue-skeleton-2" width={30} />,
          <Skeleton height={30} key="pendingIssue-skeleton-2" width={200} />,
        ],
        id: `pendingIssue-loading-${i}`,
      })
    }
    return skeleton
  }

  const handleClose = () => {
    setCurrentPage(1)
    setOpen(false)
  }

  const getPendingIssue = async () => {
    setLoading(true)

    try {
      const response = await getPendingEDIs(
        { linhas: PAGE_SIZE, paginas: currentPage },
        `${orderNumber}`,
      )

      if (response && response.length) {
        const { linhas } = response[0]

        setPendingIssues(response)
        setTotalItems(linhas)
        setTotalPages(Math.ceil(linhas / PAGE_SIZE))
      }
    } catch (error) {
      toast.error('Erro ao carregar as pendências do pedido')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!loading || open) {
      getPendingIssue()
    }
  }, [currentPage, open])

  return (
    <Modal
      closeAction={handleClose}
      open={open}
      setOpen={setOpen}
      showAction={false}
      title={`Pendência Pedido: #${orderNumber}`}
    >
      <S.Content>
        <S.DescriptionWrapper>Acompanhe aqui todas as pendências disponíveis.</S.DescriptionWrapper>

        <S.TableWrapper>
          <Table bodyData={renderBody()} headData={headValues} notScrollable rowHeight={40} />
        </S.TableWrapper>

        {pendingIssues && pendingIssues.length > 0 && (
          <Pagination
            currentPage={currentPage}
            disabled={loading}
            itemsPerPage={PAGE_SIZE}
            numberOfItems={totalItems}
            numberOfPages={totalPages}
            setCurrentPage={setCurrentPage}
          />
        )}
      </S.Content>
    </Modal>
  )
}

export default ModalPendingEDI
